import { getArtboardLabel, listArtboardIDs } from "./artboards";
import { flattenTreeToBoxes, resolveImageLayerPreview } from "./layerTree";

const MAX_COVERAGE_EDGE = 256;
const ALPHA_HOLE_THRESHOLD = 16;
const MIN_HOLE_RATIO = 0.005;

const isLocalObjectUrl = (url = "") =>
  url.startsWith("blob:") || url.startsWith("data:");

const loadImageElement = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("Failed to load image"));
    image.src = url;
  });

const loadCoverageImage = async (url) => {
  if (!url) throw new Error("Missing image URL");
  if (isLocalObjectUrl(url)) {
    return createImageBitmap(await loadImageElement(url));
  }
  const response = await fetch(url, { mode: "cors", cache: "no-store" });
  if (!response.ok) {
    throw new Error(`Failed to load image (${response.status})`);
  }
  return createImageBitmap(await response.blob());
};

const coverageCanvasSize = ({ width, height }) => {
  const sourceWidth = Math.max(1, Math.round(Number(width) || 0));
  const sourceHeight = Math.max(1, Math.round(Number(height) || 0));
  const scale = Math.min(
    1,
    MAX_COVERAGE_EDGE / Math.max(sourceWidth, sourceHeight)
  );
  return {
    width: Math.max(1, Math.round(sourceWidth * scale)),
    height: Math.max(1, Math.round(sourceHeight * scale)),
  };
};

const toCanvasBox = ({ box, canvasWidth, canvasHeight }) => ({
  x: box.nx * canvasWidth,
  y: box.ny * canvasHeight,
  width: box.nw * canvasWidth,
  height: box.nh * canvasHeight,
});

const fillBox = ({ ctx, box }) => {
  if (box.width <= 0 || box.height <= 0) return;
  ctx.fillStyle = "#000";
  ctx.fillRect(box.x, box.y, box.width, box.height);
};

const drawImageInBox = ({ ctx, image, box, fit }) => {
  if (box.width <= 0 || box.height <= 0) return;
  if (fit !== "cover") {
    ctx.drawImage(image, box.x, box.y, box.width, box.height);
    return;
  }
  const imageWidth = image.width || 1;
  const imageHeight = image.height || 1;
  const scale = Math.max(box.width / imageWidth, box.height / imageHeight);
  const drawWidth = imageWidth * scale;
  const drawHeight = imageHeight * scale;
  ctx.save();
  ctx.beginPath();
  ctx.rect(box.x, box.y, box.width, box.height);
  ctx.clip();
  ctx.drawImage(
    image,
    box.x + (box.width - drawWidth) / 2,
    box.y + (box.height - drawHeight) / 2,
    drawWidth,
    drawHeight
  );
  ctx.restore();
};

const listArtboardBoxes = ({ artboard }) => {
  const sourceWidth = Math.round(Number(artboard?.width) || 0);
  const sourceHeight = Math.round(Number(artboard?.height) || 0);
  if (sourceWidth <= 0 || sourceHeight <= 0) return [];
  return flattenTreeToBoxes({
    nodes: artboard?.nodes || {},
    nodeLayouts: artboard?.nodeLayouts || {},
    artboardWidth: sourceWidth,
    artboardHeight: sourceHeight,
  });
};

const collectCoverageImageUrls = ({ artboards = {}, assets = {} }) => {
  const urls = new Set();
  Object.values(artboards).forEach((artboard) => {
    listArtboardBoxes({ artboard }).forEach((box) => {
      if (box.type !== "image") return;
      const { assetUrl } = resolveImageLayerPreview({ box, assets });
      if (assetUrl) urls.add(assetUrl);
    });
  });
  return [...urls];
};

const prefetchCoverageImages = async ({ urls }) => {
  const cache = new Map();
  await Promise.all(
    urls.map(async (url) => {
      const image = await loadCoverageImage(url).catch(() => null);
      cache.set(url, image);
    })
  );
  return cache;
};

const paintCoverageBox = ({ ctx, box, assets, imageCache, canvasBox }) => {
  switch (box.type) {
    case "group":
    case "text":
      return;
    case "shape":
      fillBox({ ctx, box: canvasBox });
      return;
    case "image": {
      const { assetUrl } = resolveImageLayerPreview({ box, assets });
      if (!assetUrl) {
        if (box.isBackground) fillBox({ ctx, box: canvasBox });
        return;
      }
      const image = imageCache.get(assetUrl);
      if (!image) {
        fillBox({ ctx, box: canvasBox });
        return;
      }
      drawImageInBox({
        ctx,
        image,
        box: canvasBox,
        fit: box.fit,
      });
      return;
    }
    default: {
      const exhaustive = box.type;
      throw new Error(`Unhandled coverage node type: ${exhaustive}`);
    }
  }
};

const artboardHasTransparencyGap = ({ artboard, assets, imageCache }) => {
  const sourceWidth = Math.round(Number(artboard?.width) || 0);
  const sourceHeight = Math.round(Number(artboard?.height) || 0);
  if (sourceWidth <= 0 || sourceHeight <= 0) return false;

  const { width, height } = coverageCanvasSize({
    width: sourceWidth,
    height: sourceHeight,
  });
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) return false;
  ctx.clearRect(0, 0, width, height);

  const boxes = listArtboardBoxes({ artboard });
  for (let index = 0; index < boxes.length; index += 1) {
    const box = boxes[index];
    paintCoverageBox({
      ctx,
      box,
      assets,
      imageCache,
      canvasBox: toCanvasBox({
        box,
        canvasWidth: width,
        canvasHeight: height,
      }),
    });
  }

  const pixels = ctx.getImageData(0, 0, width, height).data;
  let holeCount = 0;
  const pixelCount = width * height;
  for (let index = 0; index < pixelCount; index += 1) {
    if (pixels[index * 4 + 3] < ALPHA_HOLE_THRESHOLD) holeCount += 1;
  }
  return holeCount / pixelCount > MIN_HOLE_RATIO;
};

const closeCoverageImages = ({ imageCache }) => {
  [...imageCache.values()].forEach((image) => {
    if (image && typeof image.close === "function") image.close();
  });
};

export const findArtboardsWithTransparencyGaps = async ({
  artboards = {},
  assets = {},
} = {}) => {
  const urls = collectCoverageImageUrls({ artboards, assets });
  const imageCache = await prefetchCoverageImages({ urls });
  try {
    const artboardIDs = listArtboardIDs({ artboards });
    return artboardIDs
      .map((artboardID) => {
        const artboard = artboards[artboardID];
        const hasGap = artboardHasTransparencyGap({
          artboard,
          assets,
          imageCache,
        });
        if (!hasGap) return null;
        return {
          artboardID,
          format: getArtboardLabel({ artboard, artboardID }),
        };
      })
      .filter(Boolean);
  } finally {
    closeCoverageImages({ imageCache });
  }
};
